import React, { useState, useEffect, useMemo } from 'react';
import { computeBalances } from '../../utils/settlement';

const TripExpenseSummary = ({ trip, expenses, members, appUser, onBack, onShowToast, paymentMethods = [] }) => {
    const [splitConfigs, setSplitConfigs] = useState({}); // { expenseId: { mode: 'equal'|'self'|'custom', targets: [memberId] } }
    const [step, setStep] = useState(1); // 1: Config, 2: Result
    const [activeExpenseId, setActiveExpenseId] = useState(null);
    const [showMemberSelector, setShowMemberSelector] = useState(false);
    const [netBalances, setNetBalances] = useState({}); // { memberId: amount }
    const [clearingDetails, setClearingDetails] = useState({}); // { memberId: [detail objects] }
    const [settlements, setSettlements] = useState([]); // [{ from, to, amount }]
    const [skipped, setSkipped] = useState([]); // skipped expense info
    const [imbalance, setImbalance] = useState(0);
    const [activeSelectorId, setActiveSelectorId] = useState(null); // For mode selection modal
    const tripCurrency = trip?.currency || expenses[0]?.currency || 'THB';

    // Initialize split configs
    useEffect(() => {
        const initialConfigs = {};
        expenses.forEach(e => {
            // Default: Equal split among all members
            initialConfigs[e.id] = {
                mode: 'equal',
                targets: members.map(m => m.id)
            };
        });
        setSplitConfigs(prev => ({ ...initialConfigs, ...prev }));
    }, [expenses, members]);

    const getPayerDisplay = (expense) => {
        if (expense.payer_user_id === appUser?.id) return 'ฉัน';
        const member = members.find(m => m.id === expense.payer_user_id);
        if (member) return member.display_name;
        return expense.identifier || 'ไม่ระบุ';
    };

    const getPaymentMethodLabel = (expense) => {
        if (expense.payment_method_id != null) {
            const match = paymentMethods.find(pm => String(pm.id) === String(expense.payment_method_id));
            if (match?.name) return match.name;
        }
        return expense.payment_method || 'ไม่ระบุ';
    };

    const getMemberName = (id) => members.find(m => String(m.id) === String(id))?.display_name || 'Unknown';
    const getMember = (id) => members.find(m => String(m.id) === String(id));

    const handleModeChange = (expenseId, mode) => {
        setSplitConfigs(prev => {
            const current = prev[expenseId] || {};
            let newTargets = current.targets;

            if (mode === 'equal') {
                newTargets = members.map(m => m.id);
            } else if (mode === 'self') {
                // Will be resolved to payerId during calculation
                newTargets = []; 
            } else if (mode === 'custom') {
                // Keep existing or init with all if empty
                if (!newTargets || newTargets.length === 0) newTargets = members.map(m => m.id);
            }

            return {
                ...prev,
                [expenseId]: { mode, targets: newTargets }
            };
        });

        if (mode === 'custom') {
            setActiveExpenseId(expenseId);
            setShowMemberSelector(true);
        }
    };

    const toggleMemberTarget = (memberId) => {
        setSplitConfigs(prev => {
            const config = prev[activeExpenseId];
            const currentTargets = config.targets || [];
            const newTargets = currentTargets.includes(memberId)
                ? currentTargets.filter(id => id !== memberId)
                : [...currentTargets, memberId];
            
            return {
                ...prev,
                [activeExpenseId]: { ...config, targets: newTargets }
            };
        });
    };

    const calculate = () => {
        const { balances, details, settlements, skipped, imbalance } = computeBalances(
            expenses,
            members,
            splitConfigs,
            { getPaymentMethodLabel }
        );

        setNetBalances(balances);
        setClearingDetails(details);
        setSettlements(settlements);
        setSkipped(skipped);
        setImbalance(imbalance);
        setStep(2);
    };

    const copySingleItem = (text) => {
        navigator.clipboard.writeText(text)
          .then(() => onShowToast && onShowToast('คัดลอกเรียบร้อย'))
          .catch(err => onShowToast && onShowToast('ไม่สามารถคัดลอกได้: ' + err, 'error'));
    };

    const generatePersonSummary = (memberId, name, net, details) => {
        let text = `👤 สรุปรายการของ ${name}\n`;
        text += `----------------\n`;
        
        const currency = tripCurrency;

        if (details && details.length > 0) {
            details.forEach(d => {
                if (d.type === 'credit') {
                    text += `+ ${d.amount.toLocaleString()} : ${d.note} (จ่ายให้ ${d.related})\n`;
                } else {
                    text += `- ${d.amount.toLocaleString()} : ${d.note} (จ่ายโดย ${d.related})\n`;
                }
            });
        } else {
            text += `(ไม่มีรายการค้างชำระ)\n`;
        }
        text += `----------------\n`;

        // Find settlement targets
        const relatedSettlements = settlements.filter(s => 
            (net < 0 && String(s.from) === String(memberId)) || 
            (net > 0 && String(s.to) === String(memberId))
        );

        if (relatedSettlements.length > 0) {
             const names = relatedSettlements.map(s => getMemberName(net < 0 ? s.to : s.from)).join(', ');
             const action = net >= 0 ? `🟢 ได้คืนจาก ${names}` : `🔴 ต้องจ่ายให้ ${names}`;
             text += `${action} : ${Math.abs(net).toLocaleString()} ${currency}\n`;
        } else {
             text += `${net >= 0 ? `🟢 ได้คืน` : `🔴 ต้องจ่ายเพิ่ม`} : ${Math.abs(net).toLocaleString()} ${currency}\n`;
        }
        return text;
    };

    const generateAllSummary = () => {
        let text = `สรุปการเคลียร์บิลทริป ${trip?.name || ''}\n`;
        text += `========================\n\n`;

        if (settlements.length === 0) {
            text += `✅ ไม่มียอดค้างชำระ\n`;
        } else {
            const currency = tripCurrency;
            settlements.forEach(s => {
                const fromName = getMemberName(s.from);
                const toName = getMemberName(s.to);
                text += `💸 ${fromName} จ่ายให้ ${toName} : ${s.amount.toLocaleString()} ${currency}\n`;
            });
        }
        
        return text;
    };

    const totalAmount = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    // Sort members so that the current user appears first
    const sortedMembers = useMemo(() => {
        if (!appUser?.id) return members;
        const self = members.find(m => String(m.id) === String(appUser.id));
        const others = members.filter(m => String(m.id) !== String(appUser.id));
        return self ? [self, ...others] : members;
    }, [members, appUser]);

    // Sort settlements so rows related to the current user appear first
    const sortedSettlements = useMemo(() => {
        if (!appUser?.id) return settlements;
        const withMe = settlements.filter(s => String(s.from) === String(appUser.id) || String(s.to) === String(appUser.id));
        const others = settlements.filter(s => String(s.from) !== String(appUser.id) && String(s.to) !== String(appUser.id));
        return [...withMe, ...others];
    }, [settlements, appUser]);

    return (
        <div>
            <div style={{ marginBottom: '20px' }}>
                <button 
                    onClick={step === 1 ? onBack : () => setStep(1)}
                    style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                >
                    &larr; {step === 1 ? 'กลับหน้ารายการ' : 'แก้ไขรายการ'}
                </button>
            </div>

            <h2 style={{ color: 'var(--color-primary)', textAlign: 'center', marginBottom: '20px', fontSize: '24px', fontFamily: 'var(--font-display)' }}>
                {step === 1 ? 'สรุปยอดทริป (1/2)' : 'ผลลัพธ์การเคลียร์(2/2)'}
            </h2>

            {step === 1 && (
        <div style={{ backgroundColor: 'var(--color-glass-strong)', borderRadius: '16px', boxShadow: '0 18px 36px rgba(30, 70, 90, 0.12)', overflow: 'hidden', border: '1px solid var(--color-border)', padding: '20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-primary-soft)', borderRadius: '12px' }}>
                        <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-primary)', fontSize: '16px' }}>ยอดรวมทั้งหมด</h3>
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: 'var(--color-primary)' }}>{totalAmount.toLocaleString()} {tripCurrency}</p>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                                    <th style={{ textAlign: 'left', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>รายการ</th>
                                    <th style={{ textAlign: 'center', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>วิธีหาร</th>
                                    <th style={{ textAlign: 'right', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px' }}>จำนวน</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map(item => {
                                    const config = splitConfigs[item.id] || { mode: 'equal' };
                                    return (
                                        <tr key={item.id} style={{ borderBottom: '1px solid var(--color-surface-muted)' }}>
                                            <td style={{ padding: '12px 10px' }}>
                                                <div style={{ fontWeight: '500', color: 'var(--color-text)' }}>{item.note || '-'}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>จ่ายโดย: {getPayerDisplay(item)}</div>
                                            </td>
                                            <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                <div 
                                                    onClick={() => setActiveSelectorId(item.id)}
                                                    style={{ 
                                                        padding: '10px', 
                                                        borderRadius: '8px', 
                                                        border: '1px solid var(--color-border)', 
                                                        backgroundColor: 'var(--color-glass-strong)',
                                                        fontSize: '14px',
                                                        color: 'var(--color-text)',
                                                        cursor: 'pointer',
                                                        minWidth: '120px'
                                                    }}
                                                >
                                                    {config.mode === 'equal' ? 'หารเท่าทุกคน' : 
                                                     config.mode === 'self' ? 'จ่ายเอง (ไม่คิด)' : 
                                                     'เลือกคนหาร...'}
                                                </div>
                                                {config.mode === 'custom' && (
                                                    <div 
                                                        onClick={() => { setActiveExpenseId(item.id); setShowMemberSelector(true); }}
                                                        style={{ fontSize: '11px', color: 'var(--color-primary)', marginTop: '4px', cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {config.targets?.length || 0} คน (แก้ไข)
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                                                {parseFloat(item.amount).toLocaleString()} {tripCurrency}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <button 
                            onClick={calculate}
                            style={{ 
                                padding: '12px 24px', 
                                backgroundColor: 'var(--color-primary)', 
                                backgroundImage: 'var(--color-primary-gradient)',
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontSize: '16px', 
                                fontWeight: 'bold', 
                                cursor: 'pointer', 
                                boxShadow: '0 8px 18px rgba(var(--color-primary-rgb), 0.3)' 
                            }}
                        >
                            คำนวณยอด &rarr;
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div style={{ padding: '10px' }}>
                    {(skipped.length > 0 || Math.abs(imbalance) > 0.01) && (
                        <div style={{ backgroundColor: 'var(--color-danger-soft)', padding: '12px', borderRadius: '8px', marginBottom: '14px', border: '1px solid rgba(224, 86, 75, 0.35)' }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-danger)', marginBottom: '6px' }}>⚠️ ตรวจสอบการคำนวณ</div>
                            {skipped.length > 0 && (
                                <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>
                                    {`มี ${skipped.length} รายการไม่ถูกนับ (เช่น ไม่รู้ว่าใครจ่ายหรือไม่ได้ตั้งค่าการหาร)`}
                                </div>
                            )}
                            {Math.abs(imbalance) > 0.01 && (
                                <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>
                                    ยอดรวมสุทธิคงเหลือ {imbalance.toFixed(2)} {tripCurrency} (ควรเป็น 0) — ตรวจสอบ payer/จำนวนเงิน
                                </div>
                            )}
                        </div>
                    )}

                    {/* Info Box */}
                    <div style={{ backgroundColor: 'var(--color-accent-soft)', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(74, 164, 197, 0.35)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-accent)', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '16px' }}>💡</span> คำแนะนำ
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                            คุณสามารถกดปุ่ม <strong>คัดลอก</strong> (รูปกระดาษ) หลังรายการของแต่ละคน เพื่อนำข้อความสรุปรายจ่ายไปแจ้งใน LINE/ LINE Group ได้ทันที
                        </div>
                    </div>

                    <div style={{ backgroundColor: 'var(--color-glass-strong)', borderRadius: '16px', boxShadow: '0 18px 36px rgba(30, 70, 90, 0.12)', overflow: 'hidden', border: '1px solid var(--color-border)', padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, color: 'var(--color-text)' }}>🏁 แผนการเคลียร์บิล</h3>
                            <button 
                                onClick={() => copySingleItem(generateAllSummary())}
                                style={{ 
                                    padding: '8px 12px', 
                                    backgroundColor: 'var(--color-primary)', 
                                    backgroundImage: 'var(--color-primary-gradient)',
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    fontSize: '12px', 
                                    fontWeight: 'bold', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                คัดลอกทั้งหมด
                            </button>
                        </div>

                        {/* Settlement Plan List */}
                        <div style={{ marginBottom: '25px', backgroundColor: 'var(--color-surface-muted)', borderRadius: '12px', padding: '15px' }}>
                                    {sortedSettlements.length === 0 ? (
                                        <p style={{ textAlign: 'center', color: 'var(--color-text-subtle)', margin: 0 }}>✅ ไม่มียอดค้างชำระ</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {sortedSettlements.map((s, idx) => {
                                        const isMePayer = String(s.from) === String(appUser?.id);
                                        const isMeReceiver = String(s.to) === String(appUser?.id);
                                        
                                        let leftMember = getMember(s.from);
                                        let rightMember = getMember(s.to);
                                        let actionText = 'จ่ายให้';
                                        let leftColor = 'var(--color-danger)'; // แดง (คนจ่าย)
                                        let rightColor = 'var(--color-success)'; // เขียว (คนรับ)

                                        // ถ้าเราเป็นคนรับ ให้สลับเอาเราขึ้นก่อน และเปลี่ยนข้อความ
                                        if (isMeReceiver) {
                                            leftMember = getMember(s.to); // เรา (คนรับ)
                                            rightMember = getMember(s.from); // เขา (คนจ่าย)
                                            actionText = 'ได้คืนจาก';
                                            leftColor = 'var(--color-success)'; // เรา (เขียว)
                                            rightColor = 'var(--color-danger)'; // เขา (แดง)
                                        }

                                        const currency = tripCurrency;

                                        return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', backgroundColor: 'var(--color-glass-strong)', padding: '12px', borderRadius: '10px', boxShadow: '0 12px 24px rgba(30, 70, 90, 0.12)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        {leftMember?.picture_url ? <img src={leftMember.picture_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--color-surface-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>👤</div>}
                                                        <span style={{ fontWeight: 'bold', color: leftColor }}>{(isMePayer || isMeReceiver) && leftMember?.id === appUser?.id ? 'คุณ' : (leftMember?.display_name || 'Unknown')}</span>
                                                    </div>
                                                    
                                                    <span style={{ color: 'var(--color-text-subtle)', margin: '0 2px' }}>{actionText}</span>

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        {rightMember?.picture_url ? <img src={rightMember.picture_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--color-surface-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>👤</div>}
                                                        <span style={{ fontWeight: 'bold', color: rightColor }}>{rightMember?.display_name || 'Unknown'}</span>
                                                    </div>
                                                </div>
                                                
                                                <div style={{ fontWeight: 'bold', color: 'var(--color-text)', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                                                    {s.amount.toLocaleString()} {currency}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        
                        <h4 style={{ margin: '0 0 15px 0', color: 'var(--color-text-muted)', fontSize: '14px' }}>รายละเอียดรายบุคคล (Reference)</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {sortedMembers.map(member => {
                                const balance = netBalances[member.id] || 0;
                                const details = clearingDetails[member.id] || [];
                                if (Math.abs(balance) < 0.01 && details.length === 0) return null; // Hide if no activity

                                return (
                                    <div key={member.id} style={{ display: 'flex', flexDirection: 'column', padding: '15px', backgroundColor: 'var(--color-surface-muted)', borderRadius: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {member.picture_url ? (
                                                    <img src={member.picture_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                                                ) : (
                                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👤</div>
                                                )}
                                                <span style={{ fontWeight: 'bold', color: 'var(--color-text)' }}>{member.display_name}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: balance > 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                                    {balance > 0 ? `+${balance.toLocaleString()}` : balance.toLocaleString()}
                                                </div>
                                                <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>
                                                    {(() => {
                                                        if (Math.abs(balance) < 0.01) return 'เคลียร์แล้ว';
                                                        const related = settlements.filter(s => 
                                                            (balance < 0 && String(s.from) === String(member.id)) || 
                                                            (balance > 0 && String(s.to) === String(member.id))
                                                        );
                                                        const names = related.map(s => getMemberName(balance < 0 ? s.to : s.from)).join(', ');
                                                        return balance > 0 ? `ได้คืนจาก ${names}` : `ต้องจ่ายให้ ${names}`;
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '5px' }}>
                                            <button 
                                                onClick={() => copySingleItem(generatePersonSummary(member.id, member.display_name, balance, details))}
                                                style={{ padding: '6px 12px', fontSize: '12px', cursor: 'pointer', borderRadius: '15px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                                คัดลอกรายละเอียด
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div style={{ marginTop: '30px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                            * ตัวเลขติดลบคือต้องจ่ายเพิ่ม, ตัวเลขบวกคือได้รับคืน
                        </div>
                    </div>
                </div>
            )}

            {/* Member Selector Modal */}
            {showMemberSelector && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowMemberSelector(false)}>
                    <div style={{ backgroundColor: 'var(--color-glass-strong)', width: '100%', borderRadius: '20px 20px 0 0', padding: '20px', maxHeight: '80vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
                        <style>
                            {`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}
                        </style>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text)' }}>เลือกคนหาร</h3>
                            <button onClick={() => setShowMemberSelector(false)} style={{ border: 'none', background: 'none', fontSize: '24px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>&times;</button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {members.map(m => {
                                const isSelected = splitConfigs[activeExpenseId]?.targets?.includes(m.id);
                                return (
                                    <div 
                                        key={m.id} 
                                        onClick={() => toggleMemberTarget(m.id)}
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '15px', 
                                            padding: '12px', 
                                            borderRadius: '12px', 
                                            border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                            backgroundColor: isSelected ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '1px solid var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isSelected ? 'var(--color-primary)' : 'var(--color-glass-strong)' }}>
                                            {isSelected && <span style={{ color: '#fff', fontSize: '14px' }}>✓</span>}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {m.picture_url ? <img src={m.picture_url} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%' }} /> : <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--color-border)' }}></div>}
                                            <span style={{ fontWeight: 'bold', color: 'var(--color-text)' }}>{m.display_name}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <button 
                            onClick={() => setShowMemberSelector(false)}
                            style={{ 
                                width: '100%', 
                                padding: '14px', 
                                backgroundColor: 'var(--color-primary)', 
                                backgroundImage: 'var(--color-primary-gradient)',
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '12px', 
                                fontSize: '16px', 
                                fontWeight: 'bold', 
                                marginTop: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            เสร็จสิ้น
                        </button>
                    </div>
                </div>
            )}

            {/* Mode Selector Modal */}
            {activeSelectorId && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setActiveSelectorId(null)}>
                    <div style={{ backgroundColor: 'var(--color-glass-strong)', width: '100%', borderRadius: '20px 20px 0 0', padding: '20px', animation: 'slideUp 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text)' }}>เลือกวิธีหาร</h3>
                            <button onClick={() => setActiveSelectorId(null)} style={{ border: 'none', background: 'none', fontSize: '24px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {['equal', 'self', 'custom'].map(mode => {
                                const isSelected = splitConfigs[activeSelectorId]?.mode === mode;
                                const label = mode === 'equal' ? 'หารเท่าทุกคน' : mode === 'self' ? 'จ่ายเอง (ไม่คิด)' : 'เลือกคนหาร...';
                                return (
                                    <button 
                                        key={mode}
                                        onClick={() => { handleModeChange(activeSelectorId, mode); setActiveSelectorId(null); }} 
                                        style={{ 
                                            padding: '15px', 
                                            borderRadius: '12px', 
                                            border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)', 
                                            backgroundColor: isSelected ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', 
                                            color: isSelected ? 'var(--color-primary)' : 'var(--color-text)', 
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            textAlign: 'left', 
                                            fontSize: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TripExpenseSummary;
